/**
 * Effective roles — #21587.
 *
 * The admin portal's role source. #16666 moved the fine staff roles (Operations / Infra / App) out of
 * Entra into each environment's `[dbo].[StaffRoles]` table, and the API adds them to the caller's
 * principal server-side. The access token's `roles` claim therefore no longer carries every role the
 * API authorises with, so the portal asks the API: `GET /api/v1/me/effective-roles` returns the roles
 * on the principal after claims transformation.
 *
 * Portal gating is UX only (Constitution Article IV). The API re-authorises every call.
 *
 * - **Single-flight (BR-S-EFR-006).** At most one request per signed-in account per page load,
 *   however many components call `useAuth`. Concurrent callers share one in-flight promise, and a
 *   settled success is reused.
 * - **Fail closed (BR-S-EFR-005).** Any failure — 401, 403, 5xx, timeout, network, or a malformed
 *   200 — rejects with {@link EffectiveRolesError}. The token's `roles` claim is never used as a
 *   fallback. A failure is NOT cached, so the next caller retries.
 * - **Invalidation.** {@link invalidateEffectiveRoles} drops the cache (account change, logout, and
 *   after the signed-in user saves their own roles) and notifies subscribers so they refetch.
 * - **Last-known roles (Bug #24685).** The most recent SUCCESSFUL answer for the signed-in account
 *   is kept separately from the cache. It survives an invalidation (a refetch of the same account)
 *   so a refetch that fails — the token renewal timing out after hours idle — does not strip the
 *   operator's roles and hide role-gated navigation. Only {@link forgetEffectiveRoles} (sign-out)
 *   or an answer for a different account drops it.
 */
import type { EffectiveRolesDto } from '@xcentral/api-client';
import { config } from '../config';

export const EFFECTIVE_ROLES_PATH = '/api/v1/me/effective-roles';

/** Long enough for a cold API, short enough that a hung request does not hold the shell on loading. */
export const EFFECTIVE_ROLES_TIMEOUT_MS = 15000;

/** The roles lookup failed. `status` is the HTTP status, or `null` for a network failure / timeout / bad body. */
export class EffectiveRolesError extends Error {
  readonly status: number | null;

  constructor(message: string, status: number | null) {
    super(message);
    this.name = 'EffectiveRolesError';
    this.status = status;
  }
}

/** One entry per account key: the in-flight request, or the settled successful result. */
const cache = new Map<string, Promise<string[]>>();
const listeners = new Set<() => void>();
/** Bug #24685 — the last successful answer, per the account it belongs to. */
let lastKnown: { accountKey: string; roles: string[] } | null = null;

function parseRoles(body: unknown): string[] | null {
  if (body === null || typeof body !== 'object') return null;
  const roles = (body as EffectiveRolesDto).roles;
  if (!Array.isArray(roles) || !roles.every((r) => typeof r === 'string')) return null;
  return roles;
}

async function request(getToken: () => Promise<string>): Promise<string[]> {
  const token = await getToken();
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), EFFECTIVE_ROLES_TIMEOUT_MS);
  let response: Response;
  try {
    response = await fetch(`${config.apiBaseUrl}${EFFECTIVE_ROLES_PATH}`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
    });
  } catch {
    // Network failure or the timeout above. Deliberately not echoing the error: it can carry the URL.
    throw new EffectiveRolesError('Effective roles request failed', null);
  } finally {
    clearTimeout(timer);
  }

  if (!response.ok) {
    throw new EffectiveRolesError(`Effective roles request returned ${response.status}`, response.status);
  }

  let body: unknown;
  try {
    body = await response.json();
  } catch {
    throw new EffectiveRolesError('Effective roles response was not JSON', response.status);
  }
  const roles = parseRoles(body);
  if (roles === null) {
    throw new EffectiveRolesError('Effective roles response had no roles list', response.status);
  }
  return roles;
}

/**
 * The signed-in account's effective roles, fetched once per account.
 *
 * @param accountKey identifies the signed-in account (MSAL `homeAccountId`). A different key is a
 *   different account; asking for one drops every other account's cached result.
 * @param getToken acquires the API access token. Called only by the request that actually goes out.
 */
export function fetchEffectiveRoles(
  accountKey: string,
  getToken: () => Promise<string>,
): Promise<string[]> {
  const existing = cache.get(accountKey);
  if (existing) return existing;

  // Account change: nothing cached for another account may survive.
  cache.clear();
  if (lastKnown && lastKnown.accountKey !== accountKey) lastKnown = null;

  const pending = request(getToken).then(
    (roles) => {
      lastKnown = { accountKey, roles };
      return roles;
    },
  ).catch((error: unknown) => {
    // A failure is never cached. Remove this entry only if it is still the current one, so a
    // concurrent invalidate-and-refetch is not undone.
    if (cache.get(accountKey) === pending) cache.delete(accountKey);
    throw error;
  });
  cache.set(accountKey, pending);
  return pending;
}

/**
 * Drops every cached result and tells subscribers to refetch. Called on logout, on session expiry,
 * and after the signed-in user saves their own roles on Users & Roles.
 */
export function invalidateEffectiveRoles(): void {
  cache.clear();
  for (const listener of [...listeners]) listener();
}

/**
 * Bug #24685 — the last roles successfully resolved for `accountKey`, or `null` when this account
 * has not resolved yet on this page load (or has signed out). Used to keep the operator's roles on
 * screen when a background refetch fails.
 */
export function getLastKnownEffectiveRoles(accountKey: string): string[] | null {
  return lastKnown && lastKnown.accountKey === accountKey ? lastKnown.roles : null;
}

/**
 * Sign-out: drops the cache AND the last-known roles, then tells subscribers to refetch. Unlike
 * {@link invalidateEffectiveRoles}, nothing about the previous session survives.
 */
export function forgetEffectiveRoles(): void {
  lastKnown = null;
  invalidateEffectiveRoles();
}

/**
 * Drops the last-known roles and the cache WITHOUT notifying subscribers — for the signed-out
 * (no account) branch of `useAuth`, which is itself a subscriber and must not re-trigger itself.
 */
export function clearLastKnownEffectiveRoles(): void {
  lastKnown = null;
  cache.clear();
}

/** Subscribe to invalidations. Returns the unsubscribe function. */
export function subscribeToEffectiveRolesInvalidation(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Test seam: forget everything, including subscribers. */
export function resetEffectiveRolesForTests(): void {
  cache.clear();
  lastKnown = null;
  listeners.clear();
}
